import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  Return,
  ReturnReason,
  CreateReturnRequest,
  UpdateReturnRequest
} from '../../../models/return.models';
import { Voucher } from '../../../models/voucher.models';
import { Client } from '../../../models/client.models';
import { ReturnsService } from '../../../services/returns.service';
import { ClientesService } from '../../../services/clientes.service';
import { ComprobantesService } from '../../../services/comprobantes.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-crear-devolucion',
  templateUrl: './crear-devolucion.component.html',
  styleUrls: ['./crear-devolucion.component.css']
})
export class CrearDevolucionComponent implements OnInit {

  returnForm: FormGroup;
  isEditing = false;
  isLoading = false;
  returnId?: number;
  
  // Datos para selects
  clients: Client[] = [];
  returnableVouchers: any[] = [];
  selectedVoucher: any = null;
  
  // Opciones para motivos de devolución
  reasonOptions = Object.values(ReturnReason);
  reasonLabels = {
    [ReturnReason.DEFECTIVE]: 'Producto defectuoso',
    [ReturnReason.WRONG_ITEM]: 'Producto incorrecto',
    [ReturnReason.NOT_AS_DESCRIBED]: 'No coincide con descripción',
    [ReturnReason.CUSTOMER_CHANGED_MIND]: 'Cliente cambió de opinión',
    [ReturnReason.DAMAGED_IN_SHIPPING]: 'Dañado en envío',
    [ReturnReason.OTHER]: 'Otros'
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private returnsService: ReturnsService,
    private clientesService: ClientesService,
    private comprobantesService: ComprobantesService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadClients();
    
    // Verificar si es edición
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.returnId = +params['id'];
        this.isEditing = true;
        this.loadReturn();
      }
    });
  }

  /**
   * Inicializa el formulario
   */
  initForm(): void {
    this.returnForm = this.fb.group({
      clientId: ['', Validators.required],
      originalVoucherId: [''],
      reason: ['', Validators.required],
      description: [''],
      details: this.fb.array([])
    });
  }

  /**
   * Getter para el FormArray de detalles
   */
  get detailsArray(): FormArray {
    return this.returnForm.get('details') as FormArray;
  }

  /**
   * Carga la lista de clientes
   */
  loadClients(): void {
    this.clientesService.getClientes(1, 1000, '', '').subscribe({
      next: (response) => {
        this.clients = response.data || response;
      },
      error: (error) => {
        console.error('Error loading clients:', error);
        Swal.fire('Error', 'Error al cargar clientes', 'error');
      }
    });
  }

  /**
   * Carga comprobantes retornables cuando se selecciona un cliente
   */
  onClientChange(): void {
    const clientId = this.returnForm.get('clientId')?.value;
    if (clientId) {
      this.returnsService.getReturnableVouchers(clientId).subscribe({
        next: (vouchers) => {
          this.returnableVouchers = vouchers;
        },
        error: (error) => {
          console.error('Error loading returnable vouchers:', error);
          Swal.fire('Error', 'Error al cargar comprobantes retornables', 'error');
        }
      });
    } else {
      this.returnableVouchers = [];
      this.selectedVoucher = null;
      this.clearDetails();
    }
  }

  /**
   * Maneja la selección de un comprobante original
   */
  onVoucherChange(): void {
    const voucherId = this.returnForm.get('originalVoucherId')?.value;
    if (voucherId) {
      this.selectedVoucher = this.returnableVouchers.find(v => v.id === +voucherId);
      this.loadVoucherDetails();
    } else {
      this.selectedVoucher = null;
      this.clearDetails();
    }
  }

  /**
   * Carga los detalles del comprobante seleccionado
   */
  loadVoucherDetails(): void {
    if (this.selectedVoucher && this.selectedVoucher.detail) {
      this.clearDetails();
      
      this.selectedVoucher.detail.forEach((detail: any) => {
        this.addDetailFromVoucher(detail);
      });
    }
  }

  /**
   * Agrega un detalle basado en el comprobante original
   */
  addDetailFromVoucher(voucherDetail: any): void {
    const detailGroup = this.fb.group({
      variantId: [voucherDetail.variantId, Validators.required],
      productName: [voucherDetail.productName || ''],
      variantName: [voucherDetail.variantName || ''],
      maxQuantity: [voucherDetail.quantity],
      quantity: [0, [Validators.required, Validators.min(1)]],
      unitPrice: [voucherDetail.unitPrice],
      totalPrice: [0]
    });

    // Calcular total cuando cambia la cantidad
    detailGroup.get('quantity')?.valueChanges.subscribe(quantity => {
      const unitPrice = detailGroup.get('unitPrice')?.value || 0;
      const totalPrice = quantity * unitPrice;
      detailGroup.get('totalPrice')?.setValue(totalPrice, { emitEvent: false });
    });

    this.detailsArray.push(detailGroup);
  }

  /**
   * Agrega un detalle manual (cuando no hay comprobante original)
   */
  addManualDetail(): void {
    const detailGroup = this.fb.group({
      variantId: ['', Validators.required],
      productName: [''],
      variantName: [''],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      totalPrice: [0]
    });

    // Calcular total cuando cambian cantidad o precio
    ['quantity', 'unitPrice'].forEach(field => {
      detailGroup.get(field)?.valueChanges.subscribe(() => {
        const quantity = detailGroup.get('quantity')?.value || 0;
        const unitPrice = detailGroup.get('unitPrice')?.value || 0;
        const totalPrice = quantity * unitPrice;
        detailGroup.get('totalPrice')?.setValue(totalPrice, { emitEvent: false });
      });
    });

    this.detailsArray.push(detailGroup);
  }

  /**
   * Remueve un detalle
   */
  removeDetail(index: number): void {
    this.detailsArray.removeAt(index);
  }

  /**
   * Limpia todos los detalles
   */
  clearDetails(): void {
    while (this.detailsArray.length !== 0) {
      this.detailsArray.removeAt(0);
    }
  }

  /**
   * Carga una devolución existente para edición
   */
  loadReturn(): void {
    if (!this.returnId) return;

    this.isLoading = true;
    this.returnsService.getReturn(this.returnId).subscribe({
      next: (returnData) => {
        this.populateForm(returnData);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading return:', error);
        Swal.fire('Error', 'Error al cargar la devolución', 'error');
        this.router.navigate(['/devoluciones']);
      }
    });
  }

  /**
   * Popula el formulario con datos de una devolución existente
   */
  populateForm(returnData: Return): void {
    this.returnForm.patchValue({
      clientId: returnData.clientId,
      originalVoucherId: returnData.originalVoucherId,
      reason: returnData.reason,
      description: returnData.description
    });

    // Cargar comprobantes retornables y detalles
    if (returnData.clientId) {
      this.returnsService.getReturnableVouchers(returnData.clientId).subscribe({
        next: (vouchers) => {
          this.returnableVouchers = vouchers;
          if (returnData.originalVoucherId) {
            this.selectedVoucher = vouchers.find(v => v.id === returnData.originalVoucherId);
          }
          this.loadReturnDetails(returnData.details);
        }
      });
    }
  }

  /**
   * Carga los detalles de una devolución existente
   */
  loadReturnDetails(details: any[]): void {
    this.clearDetails();
    
    details.forEach(detail => {
      const detailGroup = this.fb.group({
        variantId: [detail.variantId, Validators.required],
        productName: [detail.productName || ''],
        variantName: [detail.variantName || ''],
        quantity: [detail.quantity, [Validators.required, Validators.min(1)]],
        unitPrice: [detail.unitPrice],
        totalPrice: [detail.totalPrice]
      });

      this.detailsArray.push(detailGroup);
    });
  }

  /**
   * Calcula el total general de la devolución
   */
  calculateTotal(): number {
    return this.detailsArray.controls.reduce((total, control) => {
      return total + (control.get('totalPrice')?.value || 0);
    }, 0);
  }

  /**
   * Valida que se hayan seleccionado productos para devolver
   */
  validateDetails(): boolean {
    if (this.detailsArray.length === 0) {
      Swal.fire('Atención', 'Debe agregar al menos un producto para devolver', 'warning');
      return false;
    }

    // Validar cantidades
    for (let i = 0; i < this.detailsArray.length; i++) {
      const detail = this.detailsArray.at(i);
      const quantity = detail.get('quantity')?.value;
      const maxQuantity = detail.get('maxQuantity')?.value;

      if (quantity <= 0) {
        Swal.fire('Atención', `La cantidad debe ser mayor a 0 en el ítem ${i + 1}`, 'warning');
        return false;
      }

      if (maxQuantity && quantity > maxQuantity) {
        Swal.fire('Atención', `La cantidad no puede ser mayor a ${maxQuantity} en el ítem ${i + 1}`, 'warning');
        return false;
      }
    }

    return true;
  }

  /**
   * Guarda la devolución
   */
  save(): void {
    if (this.returnForm.invalid) {
      Swal.fire('Atención', 'Complete todos los campos requeridos', 'warning');
      return;
    }

    if (!this.validateDetails()) {
      return;
    }

    this.isLoading = true;

    const formValue = this.returnForm.value;
    const details = this.detailsArray.value.map((detail: any) => ({
      variantId: detail.variantId,
      quantity: detail.quantity,
      unitPrice: detail.unitPrice
    }));

    if (this.isEditing && this.returnId) {
      // Actualizar devolución existente
      const updateData: UpdateReturnRequest = {
        reason: formValue.reason,
        description: formValue.description,
        details: details
      };

      this.returnsService.updateReturn(this.returnId, updateData).subscribe({
        next: () => {
          Swal.fire('Éxito', 'Devolución actualizada exitosamente', 'success');
          this.router.navigate(['/devoluciones']);
        },
        error: (error) => {
          console.error('Error updating return:', error);
          Swal.fire('Error', 'Error al actualizar la devolución', 'error');
          this.isLoading = false;
        }
      });
    } else {
      // Crear nueva devolución
      const createData: CreateReturnRequest = {
        clientId: formValue.clientId,
        originalVoucherId: formValue.originalVoucherId || undefined,
        reason: formValue.reason,
        description: formValue.description,
        details: details
      };

      this.returnsService.createReturn(createData).subscribe({
        next: () => {
          Swal.fire('Éxito', 'Devolución creada exitosamente', 'success');
          this.router.navigate(['/devoluciones']);
        },
        error: (error) => {
          console.error('Error creating return:', error);
          Swal.fire('Error', 'Error al crear la devolución', 'error');
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * Cancela y vuelve a la lista
   */
  cancel(): void {
    this.router.navigate(['/devoluciones']);
  }
}